import { addClass, removeClass } from '../util.js';

/**
 * Password Change Handler
 * Handles password changes with strength validation
 */

(function () {
    'use strict';

    // DOM Elements
    var currentPassword, newPassword, confirmPassword;
    var changePasswordBtn;
    var passwordStrength, strengthText;
    var strengthBars = [];
    var form;
    /**
     * Initialize password change form
     */
    function init() {
        // Get form elements
        form = document.getElementById('passwordChangeForm');
        currentPassword = document.getElementById('currentPassword');
        newPassword = document.getElementById('newPassword');
        confirmPassword = document.getElementById('confirmPassword');
        changePasswordBtn = document.getElementById('changePasswordBtn');
        passwordStrength = document.getElementById('passwordStrength');
        strengthText = document.getElementById('strengthText');

        // Get strength bars
        for (var i = 1; i <= 5; i++) {
            strengthBars.push(document.getElementById('strength-bar-' + i));
        }

        if (!currentPassword || !changePasswordBtn) {
            return; // Not on profile page or viewing someone else's profile
        }

        // Setup event listeners
        setupEventListeners();
    }

    /**
     * Setup event listeners
     */
    function setupEventListeners() {
        // Show/update password strength on typing
        if (newPassword) {
            newPassword.addEventListener('input', function () {
                if (this.value.length > 0) {
                    updatePasswordStrength(this.value);
                    if (passwordStrength) {
                        removeClass(passwordStrength, 'hidden');
                    }
                } else {
                    if (passwordStrength) {
                        addClass(passwordStrength, 'hidden');
                    }
                }
            });
        }

        // Form Submission
        if (form) {
            form.addEventListener('submit', handlePasswordChange);
        } else if (changePasswordBtn) {
            // Fallback if form not found (should not happen with new template)
            changePasswordBtn.addEventListener('click', handlePasswordChange);
        }
    }

    /**
     * Update password strength indicator
     */
    function updatePasswordStrength(password) {
        if (!window.Validators) return;

        var result = window.Validators.password(password);
        var strength = result.strength;

        // Update strength bars
        var activeCount = strength.score;
        var colors = ['bg-red-500', 'bg-red-500', 'bg-yellow-500', 'bg-yellow-500', 'bg-green-500'];

        for (var i = 0; i < strengthBars.length; i++) {
            var bar = strengthBars[i];
            if (!bar) continue;

            removeClass(bar, 'bg-red-500 bg-yellow-500 bg-green-500 bg-gray-200');

            if (i < activeCount) {
                addClass(bar, colors[activeCount - 1]);
            } else {
                addClass(bar, 'bg-gray-200');
            }
        }

        // Update strength text
        if (strengthText) {
            var text = 'Strength: ';
            if (strength.level === 'weak') {
                text += 'Weak';
                strengthText.className = 'text-xs text-red-600';
            } else if (strength.level === 'medium') {
                text += 'Medium';
                strengthText.className = 'text-xs text-yellow-600';
            } else {
                text += 'Strong';
                strengthText.className = 'text-xs text-green-600';
            }

            // Add requirements
            text += ' - ';
            var met = [];
            var unmet = [];
            for (var key in strength.requirements) {
                var label = {
                    length: '8+ chars',
                    uppercase: 'A-Z',
                    lowercase: 'a-z',
                    number: '0-9',
                    special: '@$!%*?&'
                }[key];
                if (strength.requirements[key]) {
                    met.push(label);
                } else {
                    unmet.push(label);
                }
            }
            text += met.join(', ');
            if (unmet.length > 0) {
                text += ' | Missing: ' + unmet.join(', ');
            }

            strengthText.textContent = text;
        }
    }

    /**
     * Validate password fields
     */
    function validatePasswords() {
        if (!window.Validators) {
            console.error('Validators not loaded');
            if (window.Utils && window.Utils.showNotification) {
                window.Utils.showNotification('System error: Validation module not loaded. Please refresh the page.', 'error');
            } else {
                alert('System error: Validation module not loaded. Please refresh the page.');
            }
            return false;
        }

        // Clear previous errors
        if (currentPassword) window.Validators.clearError(currentPassword);
        if (newPassword) window.Validators.clearError(newPassword);
        if (confirmPassword) window.Validators.clearError(confirmPassword);
        var valid = true;

        // Check all fields filled
        if (!currentPassword.value) {
            var msg = (window.Validators_I18N && window.Validators_I18N.passwordRequired) || 'Current password is required';
            window.Validators.showError(currentPassword, msg);
            valid = false;
        }
        if (!newPassword.value) {
            var msg = (window.Validators_I18N && window.Validators_I18N.newPasswordRequired) || 'New password is required';
            window.Validators.showError(newPassword, msg);
            valid = false;
        }
        if (!confirmPassword.value) {
            var msg = (window.Validators_I18N && window.Validators_I18N.confirmPasswordRequired) || 'Please confirm your new password';
            window.Validators.showError(confirmPassword, msg);
            valid = false;
        }

        // Validate new password strength
        if (newPassword.value) {
            var result = window.Validators.password(newPassword.value);
            if (!result.valid) {
                window.Validators.showError(newPassword, result.message);
                valid = false;
            }
        }

        // Check passwords match
        if (newPassword.value && confirmPassword.value && newPassword.value !== confirmPassword.value) {
            var msg = (window.Validators_I18N && window.Validators_I18N.passwordsMismatch) || 'Passwords do not match';
            window.Validators.showError(confirmPassword, msg);
            valid = false;
        }

        // Check new password different from current
        if (currentPassword.value && newPassword.value && currentPassword.value === newPassword.value) {
            var msg = (window.Validators_I18N && window.Validators_I18N.passwordSameAsCurrent) || 'New password must be different from current password';
            window.Validators.showError(newPassword, msg);
            valid = false;
        }

        return valid;
    }

    /**
     * Handle password change
     */
    function handlePasswordChange(e) {
        e.preventDefault();

        if (!validatePasswords()) {
            return;
        }

        // Show loading state
        changePasswordBtn.disabled = true;
        var originalText = changePasswordBtn.textContent;
        changePasswordBtn.innerHTML = '<span class="material-symbols-outlined text-sm animate-spin align-middle mr-1">refresh</span>Changing...';

        // Prepare data
        var data = {
            currentPassword: currentPassword.value,
            newPassword: newPassword.value,
            confirmPassword: confirmPassword.value
        };

        // Send AJAX request
        fetch('/user/changePassword', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify(data)
        })
            .then(function (response) {
                return response.json();
            })
            .then(function (result) {
                changePasswordBtn.disabled = false;
                changePasswordBtn.textContent = originalText;

                if (result.success) {
                    // Clear all fields
                    currentPassword.value = '';
                    newPassword.value = '';
                    confirmPassword.value = '';

                    // Hide strength indicator
                    if (passwordStrength) {
                        addClass(passwordStrength, 'hidden');
                    }

                    // Clear any errors
                    window.Validators.clearError(currentPassword);
                    window.Validators.clearError(newPassword);
                    window.Validators.clearError(confirmPassword);

                    if (window.Utils && window.Utils.showNotification) {
                        window.Utils.showNotification(result.message || 'Password changed successfully', 'success');
                    } else {
                        alert(result.message || 'Password changed successfully');
                    }
                } else {
                    // Show error on appropriate field
                    if (result.field === 'currentPassword') {
                        window.Validators.showError(currentPassword, result.message);
                    } else if (result.field === 'newPassword') {
                        window.Validators.showError(newPassword, result.message);
                    } else if (result.field === 'confirmPassword') {
                        window.Validators.showError(confirmPassword, result.message);
                    } else {
                        if (window.Utils && window.Utils.showNotification) {
                            window.Utils.showNotification(result.message || 'Failed to change password', 'error');
                        } else {
                            alert(result.message || 'Failed to change password');
                        }
                    }
                }
            })
            .catch(function (error) {
                console.error('Error changing password:', error);
                changePasswordBtn.disabled = false;
                changePasswordBtn.textContent = originalText;
                if (window.Utils && window.Utils.showNotification) {
                    window.Utils.showNotification('Network error. Please try again.', 'error');
                } else {
                    alert('Network error. Please try again.');
                }
            });
    }

    /**
     * Show message to user
     * @deprecated Use window.Utils.showNotification instead
     */
    function showMessage(type, message) {
        if (window.Utils && window.Utils.showNotification) {
            window.Utils.showNotification(message, type);
        } else {
            alert(type === 'success' ? message : 'Error: ' + message);
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();
